import {
  HD_BODYGRAPH_DEFAULT_COLOR_CONFIG,
  HdBodygraphColorConfig,
  HdBodygraphColorConfigPresetId,
} from '../hd-bodygraph-color-config';
import {
  HD_PLANET_GRID_DEFAULT_COLOR_CONFIG,
  HdPlanetGridColorConfig,
  HdPlanetGridColorConfigPresetId,
} from '../planet-grid/hd-planet-grid-color-config';
import { HdGatePrimaryAction } from '../hd-bodygraph-chart/hd-bodygraph-chart.component';
import { HdGateInfoOpenBehavior } from '../gate-info/hd-gate-info-requested-context.model';
import { HdContentLanguage } from '../hd-content-language.service';
import { HdBridgeGateStyle } from '../hd-chart-controls-options';
import { IChingCastDistribution, IChingCastMode } from '@/app/human-design/iching/core/iching.models';
import { HdPlanetGridVisualizationMode } from '../hd-bodygraph.models';
import { HdChartButtonSize } from './hd-chart-shortcut-bar.component';
import { HdMoonTransitConnectorStyle, HdMoonTransitGateMarkerStyle } from '../../mandala/moon-transit/hd-moon-transit-view.models';
import {
  HD_LOCKS_KEYS_DEFAULT_PERIOD_ID,
  HdLocksKeysChartOverlayMode,
  HdLocksKeysSearchFilter,
  HdLocksKeysSearchSort,
  HdLocksKeysPeriodId,
} from '../../mandala/locks-keys/hd-locks-keys-metadata';
import { HdChartGateSearchSort } from '../../library/hd-chart-gate-match-sort';
import { HD_DOC_LINK_OPEN_BEHAVIOR_DEFAULT, HdDocLinkOpenBehavior } from '../doc-panel/hd-doc-link-open-behavior';
import { HD_GATE_GRAPH_PALETTE_DEFAULT, HdGateGraphPaletteId } from '../gate-graph/hd-gate-graph-palette';
import { HD_GATE_GRAPH_LAYOUT, clampHdGateGraphSpacing } from '../gate-graph/hd-gate-graph-layout';
import { HdJustNowTransitColorMode } from '../../mandala/just-now-transit/hd-just-now-transit';
import { HdMandalaQuarterColorVariant, HdMandalaZodiacRingVariant } from '../../mandala/mandala.constants';
import { HD_HOVER_HIGHLIGHT_COLOR_DEFAULT, HdHoverHighlightColor } from '../hd-hover-highlight-theme';
import {
  notifyUserDataSavedLocally,
  USER_DATA_SETTINGS_STORAGE_KEY,
} from '@common/user-data-sync/user-data-local-sync';
import { matchesPhoneViewport } from '@common/viewport/viewport-breakpoints';

export const HD_CHART_GLOBAL_SETTINGS_STORAGE_KEY = USER_DATA_SETTINGS_STORAGE_KEY;

/**
 * Scope van de "gelijkaardige charts"-matchlijsten (profiel/incarnatiekruis/
 * omgeving): de hele eigen bibliotheek, of enkel charts met een relatie-link
 * naar de bronchart. Gedefinieerd bij de settings (de bron van de keuze);
 * `HdChartMatchesService` en de match-lijsten consumeren dit type.
 */
export type HdChartMatchScope = 'library' | 'related';

/**
 * Tab-overstijgende settings van de chart-omgeving (spec
 * `docs/superpowers/specs/2026-06-11-chart-tabs-sidepanel-design.md` §6). De
 * twee `*PresetId`-velden zijn de dropdown-selecties; de config-objecten zelf
 * bevatten daarbovenop de per-veld overrides (centerBorderStyle,
 * undefinedStyle, ...) die via de aparte handlers gezet worden.
 *
 * Dit blijft de **effectieve, platte vorm** die consumers lezen en schrijven
 * (`schemaVersion: 1` incluis). De opslag is sinds de mobiele strategie
 * (docs/MOBIEL.md §4) een v2-blob met profielen per deviceklasse: gedeelde
 * velden + een `desktop`- en een `mobile`-profiel voor de layout-/weergave-
 * subset. `loadHdChartGlobalSettings()` levert de platte vorm voor het actieve
 * profiel; `saveHdChartGlobalSettings()` splitst hem terug — het andere
 * profiel blijft daarbij onaangeroerd, zodat telefoon en desktop elkaars
 * weergave-instellingen niet meer overschrijven via de account-sync.
 */
export interface HdChartGlobalSettingsV1 {
  schemaVersion: 1;
  colorConfig: HdBodygraphColorConfig;
  colorConfigPresetId: HdBodygraphColorConfigPresetId;
  planetGridColorConfig: HdPlanetGridColorConfig;
  planetGridColorConfigPresetId: HdPlanetGridColorConfigPresetId;
  showPlanetGrid: boolean;
  /** Eenvoudige (classic) planeetgrid-variant i.p.v. de uitgebreide grid. */
  simplePlanetGrid: boolean;
  /**
   * Geavanceerde "Resonance"-variant van de Lunar Planetary Square (advanced
   * LPS). Vervangt binnen de volledige planeetgrid de LPS-sectie door de
   * uitgebreide Resonance-weergave (verticals/laterals/legende).
   */
  advancedLpsGrid: boolean;
  /**
   * Mini-densiteit van de volledige planeetgrid (docs/MOBIEL.md §9): compacte
   * blokken met enkel planeetsymbool + fixing-pijlen en poort.lijn, zodat op
   * phone design- én personality-grid naast elkaar passen.
   */
  miniPlanetGrid: boolean;
  /** Toont het informatieve Chiron-item onderaan de eenvoudige planeetgrid. */
  showChiron: boolean;
  /**
   * Astro posities (Chiron, AC, DC, MC, IC, Black Moon Lilith) als rij onder de volledige
   * planetengrid, klassiek en geavanceerde LPS; niet bij de eenvoudige grid (spec 2026-10-06).
   * Standaard uit: geen HD-gegevens, dus bewust zelf aan te zetten (Steven 2026-10-06).
   */
  showAstroPositions: boolean;
  /** Mini-planeetblokjes bij de poorten op de bodygraph (gate-block overlay). */
  showGateBlocks: boolean;
  /**
   * Weergavemodus van de uitgebreide planeetgrid: `classic` (enkel cijfers, de
   * huidige weergave) of `extended` (met PHS-iconen). Default `extended`.
   */
  planetGridVisualizationMode: HdPlanetGridVisualizationMode;
  /** Bepaalt of niet-HD-features, zoals Gene Keys, beschikbaar zijn. */
  includeNonHdFeatures: boolean;
  showGeneKeys: boolean;
  showHdKeynotes: boolean;
  /** `true`: enkel de direct gehoverde kaart; `false`: chart en planeetgrid samen. */
  keynotesAtHoverOnly: boolean;
  showBothCenterStateSections: boolean;
  showCenterCheatSheet: boolean;
  /**
   * Info/Edit-stand van de chart (shortcut-bar): `show-info` = klik op een poort
   * opent info én de hover-highlight werkt; `configure` = klik configureert en de
   * hover-highlight staat uit. Default `show-info`: bekijken is de regel, de
   * editor zet zelf `configure`. Een reset van de settings laat deze stand met
   * rust (zie `HdChartSettingsStore.resetToDefaults`).
   */
  chartGatePrimaryAction: HdGatePrimaryAction;
  chartContextMenuEnabled: boolean;
  /**
   * Hover op een kanaalstuk van een volledig defined kanaal highlight het
   * héle kanaal i.p.v. enkel de poorthelft (K-toets togglet dit). Zie spec
   * `2026-07-02-hd-kanaal-highlight-en-kanaal-info-design.md`.
   */
  channelHighlightEnabled: boolean;
  /**
   * Dimmen bij hover (default aan). `false`: bij hover/freeze dimt niets —
   * enkel de standaard blauwe-rand-highlights worden getoond, en élk matchend
   * element (ook extra activaties van dezelfde poort) krijgt een blauwe rand.
   */
  dimOnHover: boolean;
  /** Kleur van de hover-highlightranden: klassiek blauw of fel groen. */
  hoverHighlightColor: HdHoverHighlightColor;
  /**
   * Het kanaalstuk van een gehighlighte poort, of het hele kanaal wanneer beide
   * poorten gehighlight zijn, krijgt een outline in de hoverkleur. Default aan
   * (Steven 2026-10-14).
   */
  highlightGatePieces: boolean;
  /**
   * Aan (en "dimmen bij hover" aan): enkel de kant tegenover de hover-bron dimt —
   * hover in de planeetgrid dimt de chart, hover in de chart dimt de planeetgrid.
   * De highlights zelf blijven ongewijzigd. Default aan (Steven 2026-10-14).
   */
  dimOneSideOnly: boolean;
  /**
   * Community-content (zoals de Line Companion) tonen. Opt-in, default uit:
   * deze teksten worden gedeeld door communityleden en maken geen deel uit
   * van de standaard applicatie. Zichtbaarheid vereist daarnaast de
   * UPAM-feature van het pack (zie `CommunityContentAccessService`).
   */
  communityContentEnabled: boolean;
  /**
   * Alternatieve I Tjing-interpretaties (uit "The Book of Lines" van Chetan
   * Parkyn) als extra regel bij de exaltation/detriment van elke lijn, zowel in
   * de gate-info-panelen als op de I Tjing-cheat-sheet-kaarten. Opt-in,
   * default uit.
   */
  alternativeIChingEnabled: boolean;
  /**
   * Ra Uru Hu's lijnteksten bij elke lijn: de blauwe lijnbeschrijving (met
   * subtekst/vervolg onder "extra info") en de White/Black Book-keynotes bij de
   * exaltation/detriment. Default aan (de bestaande weergave); uitzetten laat
   * enkel de andere gekozen bronnen staan.
   */
  raLineTextsEnabled: boolean;
  /**
   * Eigen lijnteksten van Mechanics in Motion (eigen lijnbeschrijving plus
   * eigen exaltation-/detriment-regel per lijn), juridisch veilige fallback
   * voor de Ra- en Chetan-teksten. Opt-in, default uit; los te combineren met
   * {@link raLineTextsEnabled} en {@link alternativeIChingEnabled}.
   */
  ownLineTextsEnabled: boolean;
  /**
   * Classic I Ching (Wilhelm NL / Legge EN) bij de poorten en als pagina.
   * Opt-in, default uit. Telt alleen samen met de UPAM-feature
   * `EnableClassicIChing` (zie `HdIChingAccessService`).
   */
  classicIChingEnabled: boolean;
  /** Laatst gekozen worpmodus op de Classic I Ching-pagina. */
  ichingCastMode: IChingCastMode;
  /** Kansverdeling van de virtuele worp op de Classic I Ching-pagina: munten of duizendblad. */
  ichingCastDistribution: IChingCastDistribution;
  /** Introductie op de Classic I Ching-pagina ingeklapt (na het eerste bezoek). */
  ichingIntroCollapsed: boolean;
  /** Classic I Ching-doorklikiconen bij de chart (Geavanceerd, composite); telt alleen met `classicIChingEnabled`. */
  classicIChingIconsChart: boolean;
  /** Classic I Ching-doorklikiconen bij transits (kalender, Just Now, ephemeris); telt alleen met `classicIChingEnabled`. */
  classicIChingIconsTransits: boolean;
  gateInfoOpenBehavior: HdGateInfoOpenBehavior;
  /** Kleurenramp van de radiale poort-visualisatie (gate graph). */
  gateGraphPalette: HdGateGraphPaletteId;
  /**
   * Ademruimte rond de lijncirkels van de radiale poort-visualisatie: de
   * afstand lijn → kleur in layout-eenheden. Onder 45 kruipen de basis-bolletjes
   * tegen hun eigen lijncirkel aan, boven 70 wordt de tekening onleesbaar klein.
   */
  gateGraphSpacing: number;
  /**
   * Open-gedrag van markdown-links in de gedockte doc-info-panelen (circuit /
   * stream / overzicht): `same-panel` (in-place met terug-history) of
   * `new-panel` (apart gedockt paneel per link). Default `same-panel`.
   */
  docLinkOpenBehavior: HdDocLinkOpenBehavior;
  /**
   * Wanneer `true` opent een klik op een poort vanuit het kanaal-info paneel
   * die poort als een apart (gedockt) paneel in het rechter side-panel — de
   * kanaal-dialog blijft staan i.p.v. in-place naar de poort-weergave te
   * wisselen. Default `true` (aparte poort-tab in het dock).
   */
  gateInfoFromChannelSeparateToDock: boolean;
  /** Volledige (advanced) brugpoort-priority-sortering aan/uit. */
  advancedBridgeCalculation: boolean;
  /** Weergavestijl van brugpoort-nummers (gevulde badge of gekleurd bolletje). */
  bridgeGateStyle: HdBridgeGateStyle;
  /**
   * Vorm van de verbindingslijn maanpoort↔partnerpoort in de maan-transit
   * modus: licht naar binnen gebogen (default) of recht. Spec
   * `2026-07-06-hd-moon-transit-chart-design.md` §6.2/§6.5.
   */
  moonTransitConnectorStyle: HdMoonTransitConnectorStyle;
  /**
   * Markering van de maanpoorten in het wiel: cirkel om het poortnummer
   * (default) of venster-kader om het poortsegment (finetune-ronde 3).
   */
  moonTransitGateMarkerStyle: HdMoonTransitGateMarkerStyle;
  /** Bronkleuren van de Just Now-transit overlay; default bronkleuren met groene arcering. */
  justNowTransitColorMode: HdJustNowTransitColorMode;
  /**
   * Geselecteerde keys-periode van de Locks & Keys-modus (spec 2026-08-27).
   * De modus zelf is — net als de transit-modi — sessie-lokaal; de periode is
   * een inhoudelijke voorkeur en blijft bewaard. Het periodepaar zelf niet:
   * dat start altijd op Planning en Sleeping Phoenix, met deze kant actief.
   */
  locksKeysPeriod: HdLocksKeysPeriodId;
  /**
   * Locks & keys op de bodygraph buiten de modus (drie-standen-toggle in het
   * Geavanceerd-paneel): uit, alle locks + keys van de periode, of enkel de
   * poorten die de chart activeert.
   */
  locksKeysChartOverlay: HdLocksKeysChartOverlayMode;
  /** L&K-zoekresultaten: eerst op aantal relaties, of enkel alfabetisch. */
  locksKeysSearchSort: HdLocksKeysSearchSort;
  /** L&K-zoekresultaten: alle relaties, of enkel die via zon/aarde/knopen (kruis). */
  locksKeysSearchFilter: HdLocksKeysSearchFilter;
  /** Kleurvariant van de mandala-kwartaalband: zacht (A) of voller (B). */
  mandalaQuarterColorVariant: HdMandalaQuarterColorVariant;
  /** Dierenriem-band: donker (default, witte glyphs) of licht (donkere glyphs). */
  mandalaZodiacRingVariant: HdMandalaZodiacRingVariant;
  /** Gefadede centerkleur in de mandala-slices van geactiveerde poorten. */
  mandalaSliceCenterFade: boolean;
  /**
   * Rechter configuratie-/dock-paneel ingeklapt (meer ruimte voor de chart op
   * smallere schermen). Zolang dit aan staat vallen gedockt-openende
   * info-panelen tijdelijk terug op het gewone zwevende gedrag.
   */
  configPanelCollapsed: boolean;
  /**
   * Breedte (px) van het rechterpaneel, gekozen met de sleeplijn tussen chart en
   * paneel (desktop). `undefined` = de standaardbreedte, tevens het minimum.
   */
  configPanelWidth: number | undefined;
  contentLangOverride: HdContentLanguage | null;
  settingsPanelOpen: boolean;
  /** Grootte van de shortcut-bar-knoppen: `sm` (klein) of `xs` (extra klein). */
  shortcutButtonSize: HdChartButtonSize;
  /** Weergavemodus van de Nu & Straks-sectie: kaarten (default) of lijst. */
  transitsNowNextViewMode: 'cards' | 'list';
  /** Sortering van de Nu & Straks-sectie: planeetvolgorde (default) of eerstvolgende shift. */
  transitsNowNextSortMode: 'planetOrder' | 'nextShift';
  /** Zoom-niveau van de transit-lanes ('month' is geparkeerd — valt dan terug op de default). */
  transitsLanesZoom: 'hour' | 'day' | 'week' | 'month';
  /**
   * Weergave van het transits-paneel: de verticale dag-tijdlijn (default sinds
   * 2026-09-26) of de horizontale weergave (Nu & Straks + ephemeris-lanes).
   */
  transitsViewMode: 'vertical' | 'horizontal';
  /**
   * Planeetglyphs in de maandkalender van de verticale transits-weergave:
   * enkel poortwissels (default, rustig) of ook lijnwissels.
   */
  transitsCalendarBadgeGranularity: 'gate' | 'line';
  /**
   * Poortinfo bij de wisselblokken van de verticale transits-weergave: enkel
   * de poortlink (default), een badge met de twee keynotes, of een extra rij
   * met de keynotes en de korte I Tjing-beschrijving van de poort.
   */
  transitsGateInfoMode: 'link' | 'badge' | 'row';
  /** Scope van de "gelijkaardige charts"-matchlijsten (hele bibliotheek of enkel gerelateerd). */
  chartMatchScope: HdChartMatchScope;
  /** Poortzoektocht in de poort-info: zwaarste activaties eerst (gewicht), of alfabetisch. */
  gateSearchSort: HdChartGateSearchSort;
  /**
   * Weergave van de sectie Eigenschappen in het basis-paneel: tegels (default sinds
   * 2026-10-13, twee per rij met een icoon per eigenschap) of de klassieke lijst.
   */
  basicPropertiesView: 'tiles' | 'list';
  /**
   * Basis-paneel gekoppeld aan de chart-hover (instellingenpaneel, interactie): hover
   * op een center- of activatie-kaartje licht het center/de poort op in chart en grid
   * en omgekeerd, Ctrl/Cmd+klik bevriest. Zwaar (elke kaartje-kruising = een
   * chart-highlight), daarom default uit: enkel een eenvoudige hover, klik → info.
   */
  basicPanelChartLink: boolean;
  /** Mini-labels (titel boven stip/balkje) op de levenstijdlijn (spec levenstijdlijn §2.8). */
  lifeTimelineLabels: boolean;
  /** Kleurmodus van de levenstijdlijn: momentkleur of stemming-kleur (spec §2.3). */
  lifeTimelineColorMode: 'moment' | 'mood';
  /** Weergave van de tijdlijn-pagina: tijdlijn of kalender (spec §2.7). */
  lifeTimelineView: 'timeline' | 'calendar';
  /** Transit-strip onder de levenstijdlijn aan/uit (spec §2.5). */
  lifeTimelineTransitStrip: boolean;
  /**
   * Lijst van alle incarnatiekruisen in het algemene IX-paneel: gegroepeerd per
   * kwartaal (default) of één lijst op poortnummer met het kwartaal als kolom.
   */
  incarnationCrossListView: 'quarter' | 'gate';
  /** Volgorde binnen de kwartaalgroepen: mandala-volgorde (default) of poortnummer. */
  incarnationCrossListOrder: 'mandala' | 'gate';
  /**
   * Laatste keuzes in de PDF-printdialoog (spec 2026-10-21-chart-pdf-print): kleurenschema van de
   * centers (`app` = de huidige app-instellingen, anders een bodygraph-preset-id), welke naam in de
   * kop komt, Basis- of Regulier-layout, en de center-keynotes. Een eigen naam zelf wordt niet
   * bewaard: die hoort bij één chart.
   */
  printColorScheme: string;
  printNameMode: 'firstName' | 'fullName' | 'custom';
  printLayout: 'basic' | 'regular' | 'mandala';
  printCenterKeynotes: boolean;
}

export function createDefaultHdChartGlobalSettings(): HdChartGlobalSettingsV1 {
  // De twee config-objecten worden by-reference gedeeld met de module-
  // constanten: consumers moeten ze VERVANGEN (signal.set/.update met spread),
  // nooit in-place muteren — anders corrumperen ze de gedeelde defaults.
  return {
    schemaVersion: 1,
    colorConfig: HD_BODYGRAPH_DEFAULT_COLOR_CONFIG,
    colorConfigPresetId: 'warm-dusk',
    // 'warm-dusk' matcht HD_PLANET_GRID_DEFAULT_COLOR_CONFIG; de oude controls-
    // bar toonde hier foutief 'warm-dusk-subtle' terwijl warm-dusk rendert.
    planetGridColorConfig: HD_PLANET_GRID_DEFAULT_COLOR_CONFIG,
    planetGridColorConfigPresetId: 'warm-dusk',
    showPlanetGrid: true,
    simplePlanetGrid: false,
    // Default uit (finetune 2026-09-15): de klassieke volledige grid is de
    // standaard; eenvoudiger (simple) of geavanceerder (Resonance-LPS) kies je
    // bewust via de shortcut-knoppen.
    advancedLpsGrid: false,
    miniPlanetGrid: false,
    showChiron: true,
    showAstroPositions: false,
    showGateBlocks: false,
    planetGridVisualizationMode: 'extended',
    includeNonHdFeatures: false,
    showGeneKeys: false,
    showHdKeynotes: false,
    keynotesAtHoverOnly: true,
    showBothCenterStateSections: true,
    showCenterCheatSheet: false,
    chartGatePrimaryAction: 'show-info',
    chartContextMenuEnabled: true,
    channelHighlightEnabled: true,
    dimOnHover: true,
    hoverHighlightColor: HD_HOVER_HIGHLIGHT_COLOR_DEFAULT,
    highlightGatePieces: true,
    dimOneSideOnly: true,
    communityContentEnabled: false,
    alternativeIChingEnabled: false,
    raLineTextsEnabled: true,
    ownLineTextsEnabled: false,
    classicIChingEnabled: false,
    ichingCastMode: 'coins',
    ichingCastDistribution: 'coins',
    ichingIntroCollapsed: false,
    classicIChingIconsChart: true,
    classicIChingIconsTransits: true,
    // Standaard meteen volledig gedockt (behoud); undocken doet de gebruiker zelf.
    gateInfoOpenBehavior: 'full-docked-keep',
    gateGraphPalette: HD_GATE_GRAPH_PALETTE_DEFAULT,
    gateGraphSpacing: HD_GATE_GRAPH_LAYOUT.spacing.default,
    docLinkOpenBehavior: HD_DOC_LINK_OPEN_BEHAVIOR_DEFAULT,
    gateInfoFromChannelSeparateToDock: true,
    advancedBridgeCalculation: true,
    bridgeGateStyle: 'badge',
    moonTransitConnectorStyle: 'curved',
    moonTransitGateMarkerStyle: 'ring',
    justNowTransitColorMode: 'three-color',
    locksKeysPeriod: HD_LOCKS_KEYS_DEFAULT_PERIOD_ID,
    locksKeysChartOverlay: 'off',
    locksKeysSearchSort: 'relations',
    locksKeysSearchFilter: 'all',
    mandalaQuarterColorVariant: 'light',
    mandalaZodiacRingVariant: 'dark',
    // Default áán (besluit 2026-08-20): de gefadede centerkleur in de slices
    // is de standaardweergave; uitzetten kan via het settings-panel.
    mandalaSliceCenterFade: true,
    configPanelCollapsed: false,
    configPanelWidth: undefined,
    contentLangOverride: null,
    settingsPanelOpen: true,
    shortcutButtonSize: 'sm',
    transitsNowNextViewMode: 'cards',
    transitsNowNextSortMode: 'planetOrder',
    transitsLanesZoom: 'day',
    transitsViewMode: 'vertical',
    transitsCalendarBadgeGranularity: 'gate',
    transitsGateInfoMode: 'link',
    chartMatchScope: 'library',
    gateSearchSort: 'weight',
    basicPropertiesView: 'tiles',
    basicPanelChartLink: false,
    lifeTimelineLabels: true,
    lifeTimelineColorMode: 'moment',
    lifeTimelineView: 'timeline',
    lifeTimelineTransitStrip: false,
    incarnationCrossListView: 'quarter',
    incarnationCrossListOrder: 'mandala',
    printColorScheme: 'app',
    printNameMode: 'firstName',
    printLayout: 'basic',
    printCenterKeynotes: false,
  };
}

/**
 * Profielen per deviceklasse (docs/MOBIEL.md §4). `mobile` hoort bij de
 * phone-viewport; tablet gebruikt — net als de layout — het desktop-profiel.
 */
export type HdChartSettingsProfileId = 'desktop' | 'mobile';

/**
 * De layout-/weergavevelden die per deviceklasse verschillen. Al het overige
 * (kleurpresets, inhoud, interactiegedrag, transit-voorkeuren, ...) is gedeeld
 * tussen de profielen. Een veld hierheen verhuizen is backwards-compatible:
 * de gedeelde waarde blijft gelden tot een profiel hem overschrijft.
 */
const HD_CHART_PROFILE_FIELDS = [
  'showPlanetGrid',
  'simplePlanetGrid',
  'advancedLpsGrid',
  'miniPlanetGrid',
  'planetGridVisualizationMode',
  'showGateBlocks',
  'keynotesAtHoverOnly',
  'configPanelCollapsed',
  'configPanelWidth',
  'settingsPanelOpen',
  'shortcutButtonSize',
] as const satisfies readonly (keyof HdChartGlobalSettingsV1)[];

type HdChartProfileField = (typeof HD_CHART_PROFILE_FIELDS)[number];
export type HdChartProfileSettings = Pick<HdChartGlobalSettingsV1, HdChartProfileField>;

interface HdChartGlobalSettingsV2Stored {
  schemaVersion: 2;
  /** Zie {@link HD_CHART_SETTINGS_DEFAULTS_VERSION}; ontbreekt in blobs van vóór 0.9.1. */
  defaultsVersion?: number;
  shared: Omit<HdChartGlobalSettingsV1, HdChartProfileField | 'schemaVersion'>;
  desktop: HdChartProfileSettings;
  mobile: HdChartProfileSettings;
}

/**
 * Versie van de standaardwaarden. Een opgeslagen blob bevat álle velden, dus een
 * gewijzigde default bereikt bestaande gebruikers nooit vanzelf. Hoog deze versie
 * op (en vul {@link HD_CHART_DEFAULTS_UPGRADES} aan) wanneer zij bij de volgende
 * update bewust de nieuwe defaults moeten krijgen voor een vaste set velden; de
 * upgrade loopt eenmalig bij het lezen en schrijft de blob terug (account-sync).
 */
export const HD_CHART_SETTINGS_DEFAULTS_VERSION = 1;

/** Per defaults-versie: de gedeelde velden die eenmalig op hun nieuwe default gezet worden. */
const HD_CHART_DEFAULTS_UPGRADES: Readonly<Record<number, readonly (keyof HdChartGlobalSettingsV2Stored['shared'])[]>> = {
  // 1 (0.9.1, Steven 2026-10-14): hoverkleur groen, poortstukken aan, één kant dimmen aan,
  // geen niet-HD-features, kleurenpalet warm-dusk, mandala-kwartaalkleuren C, Info-stand.
  1: [
    'hoverHighlightColor', 'highlightGatePieces', 'dimOneSideOnly', 'includeNonHdFeatures',
    'colorConfig', 'colorConfigPresetId', 'mandalaQuarterColorVariant', 'chartGatePrimaryAction',
  ],
};

function upgradeStoredDefaults(stored: HdChartGlobalSettingsV2Stored): HdChartGlobalSettingsV2Stored {
  const from = stored.defaultsVersion ?? 0;
  if (from >= HD_CHART_SETTINGS_DEFAULTS_VERSION) {
    return stored;
  }
  const defaults = pickSharedFields(createDefaultHdChartGlobalSettings()) as Record<string, unknown>;
  const shared = { ...stored.shared } as Record<string, unknown>;
  for (let version = from + 1; version <= HD_CHART_SETTINGS_DEFAULTS_VERSION; version++) {
    for (const field of HD_CHART_DEFAULTS_UPGRADES[version] ?? []) {
      shared[field] = defaults[field];
    }
  }
  return {
    ...stored,
    shared: shared as HdChartGlobalSettingsV2Stored['shared'],
    defaultsVersion: HD_CHART_SETTINGS_DEFAULTS_VERSION,
  };
}

/**
 * Mobiele profiel-defaults als afwijking van de desktop-defaults: de
 * mini-grid als standaard (design + personality passen dan naast elkaar,
 * besluit 2026-09-16), geen advanced LPS (past ruimtelijk niet op phone),
 * keynotes niet hover-only (hover bestaat niet op touch) en het
 * settings-paneel niet automatisch open.
 */
const HD_CHART_MOBILE_PROFILE_OVERRIDES: Partial<HdChartProfileSettings> = {
  advancedLpsGrid: false,
  miniPlanetGrid: true,
  keynotesAtHoverOnly: false,
  settingsPanelOpen: false,
};

/** Het settings-profiel dat bij de huidige viewport hoort. */
export function activeHdChartSettingsProfile(): HdChartSettingsProfileId {
  return matchesPhoneViewport() ? 'mobile' : 'desktop';
}

function pickProfileFields(flat: HdChartGlobalSettingsV1): HdChartProfileSettings {
  const profile = {} as Record<string, unknown>;
  for (const field of HD_CHART_PROFILE_FIELDS) {
    profile[field] = flat[field];
  }
  return profile as HdChartProfileSettings;
}

function pickSharedFields(flat: HdChartGlobalSettingsV1): HdChartGlobalSettingsV2Stored['shared'] {
  const shared = { ...flat } as Record<string, unknown>;
  delete shared['schemaVersion'];
  for (const field of HD_CHART_PROFILE_FIELDS) {
    delete shared[field];
  }
  return shared as HdChartGlobalSettingsV2Stored['shared'];
}

function createDefaultProfile(profile: HdChartSettingsProfileId): HdChartProfileSettings {
  const desktopDefaults = pickProfileFields(createDefaultHdChartGlobalSettings());
  return profile === 'mobile'
    ? { ...desktopDefaults, ...HD_CHART_MOBILE_PROFILE_OVERRIDES }
    : desktopDefaults;
}

function createDefaultStoredV2(): HdChartGlobalSettingsV2Stored {
  const defaults = createDefaultHdChartGlobalSettings();
  return {
    schemaVersion: 2,
    defaultsVersion: HD_CHART_SETTINGS_DEFAULTS_VERSION,
    shared: pickSharedFields(defaults),
    desktop: createDefaultProfile('desktop'),
    mobile: createDefaultProfile('mobile'),
  };
}

/** v1-blob (pre-mobiel) → v2: de bestaande settings worden het desktop-profiel. */
function migrateV1ToV2(parsed: HdChartGlobalSettingsV1): HdChartGlobalSettingsV2Stored {
  const flat = { ...createDefaultHdChartGlobalSettings(), ...parsed };
  return {
    schemaVersion: 2,
    shared: pickSharedFields(flat),
    desktop: pickProfileFields(flat),
    mobile: createDefaultProfile('mobile'),
  };
}

function writeStoredV2(stored: HdChartGlobalSettingsV2Stored): void {
  try {
    const json = JSON.stringify(stored);
    localStorage.setItem(HD_CHART_GLOBAL_SETTINGS_STORAGE_KEY, json);
    notifyUserDataSavedLocally('settings', json);
  } catch {
    console.warn('hdChartGlobalSettings: wegschrijven naar localStorage mislukt.');
  }
}

/**
 * Leest de opgeslagen blob als v2. Een v1-blob (van vóór de mobiele strategie,
 * of via de account-sync van een oudere client) wordt ter plekke gemigreerd en
 * teruggeschreven. `undefined` bij afwezig/corrupt/onbekend.
 */
function readStoredV2(): HdChartGlobalSettingsV2Stored | undefined {
  const raw = localStorage.getItem(HD_CHART_GLOBAL_SETTINGS_STORAGE_KEY);
  if (!raw) {
    return undefined;
  }
  try {
    const parsed = JSON.parse(raw) as { schemaVersion?: number };
    if (parsed?.schemaVersion === 2) {
      // Nieuwe defaults voor bestaande gebruikers: eenmalig toepassen en terugschrijven.
      const stored = parsed as HdChartGlobalSettingsV2Stored;
      const upgraded = upgradeStoredDefaults(stored);
      if (upgraded !== stored) {
        writeStoredV2(upgraded);
      }
      return upgraded;
    }
    if (parsed?.schemaVersion === 1) {
      const migrated = upgradeStoredDefaults(migrateV1ToV2(parsed as HdChartGlobalSettingsV1));
      writeStoredV2(migrated);
      return migrated;
    }
    return undefined;
  } catch {
    console.warn('hdChartGlobalSettings: corrupte localStorage-inhoud genegeerd.');
    return undefined;
  }
}

export function loadHdChartGlobalSettings(
  profile: HdChartSettingsProfileId = activeHdChartSettingsProfile(),
): HdChartGlobalSettingsV1 {
  const stored = readStoredV2();
  const defaults = createDefaultHdChartGlobalSettings();
  // Merge over defaults zodat later toegevoegde velden een waarde hebben; de
  // mobiele profiel-defaults gelden vóór de opgeslagen profielvelden.
  const merged: HdChartGlobalSettingsV1 = {
    ...defaults,
    ...(profile === 'mobile' ? HD_CHART_MOBILE_PROFILE_OVERRIDES : undefined),
    ...stored?.shared,
    ...stored?.[profile],
    schemaVersion: 1,
  };
  // De spreiding van de gate graph is numeriek en instelbaar; een waarde
  // buiten het geldige bereik zou cirkels laten overlappen.
  merged.gateGraphSpacing = clampHdGateGraphSpacing(merged.gateGraphSpacing);
  // De mini-grid is een phone-weergave: de toggle zit enkel in de mobiele
  // toolbar. Een `true` in het desktop-profiel is dus altijd vervuiling (de
  // store schreef tot 2026-09-26 na een viewport-wissel mobiele waarden in het
  // desktop-profiel) en valt op desktop niet meer uit te zetten. Negeren laat
  // zulke accounts zichzelf herstellen bij de volgende write.
  if (profile === 'desktop') {
    merged.miniPlanetGrid = false;
  }
  return merged;
}

export function saveHdChartGlobalSettings(
  settings: HdChartGlobalSettingsV1,
  profile: HdChartSettingsProfileId = activeHdChartSettingsProfile(),
): void {
  const base = readStoredV2() ?? createDefaultStoredV2();
  writeStoredV2({
    ...base,
    schemaVersion: 2,
    defaultsVersion: HD_CHART_SETTINGS_DEFAULTS_VERSION,
    shared: pickSharedFields(settings),
    [profile]: { ...base[profile], ...pickProfileFields(settings) },
  });
}

/**
 * Velden die niet door de settings-store maar door hun eigen component beheerd
 * worden (transits-panelen, levenstijdlijn-pagina, basis-paneel). Een full
 * persist van de store neemt de opgeslagen waarden over i.p.v. ze op de
 * default terug te zetten.
 */
export function pickExternallyManagedSettings(): Pick<
  HdChartGlobalSettingsV1,
  'transitsNowNextViewMode' | 'transitsNowNextSortMode' | 'transitsLanesZoom'
  | 'transitsViewMode' | 'transitsCalendarBadgeGranularity' | 'transitsGateInfoMode'
  | 'lifeTimelineLabels' | 'lifeTimelineColorMode' | 'lifeTimelineView' | 'lifeTimelineTransitStrip'
  | 'basicPropertiesView' | 'incarnationCrossListView' | 'incarnationCrossListOrder'
  | 'printColorScheme' | 'printNameMode' | 'printLayout' | 'printCenterKeynotes'
> {
  const stored = loadHdChartGlobalSettings();
  return {
    basicPropertiesView: stored.basicPropertiesView,
    transitsNowNextViewMode: stored.transitsNowNextViewMode,
    transitsNowNextSortMode: stored.transitsNowNextSortMode,
    transitsLanesZoom: stored.transitsLanesZoom,
    transitsViewMode: stored.transitsViewMode,
    transitsCalendarBadgeGranularity: stored.transitsCalendarBadgeGranularity,
    transitsGateInfoMode: stored.transitsGateInfoMode,
    lifeTimelineLabels: stored.lifeTimelineLabels,
    lifeTimelineColorMode: stored.lifeTimelineColorMode,
    lifeTimelineView: stored.lifeTimelineView,
    lifeTimelineTransitStrip: stored.lifeTimelineTransitStrip,
    incarnationCrossListView: stored.incarnationCrossListView,
    incarnationCrossListOrder: stored.incarnationCrossListOrder,
    printColorScheme: stored.printColorScheme,
    printNameMode: stored.printNameMode,
    printLayout: stored.printLayout,
    printCenterKeynotes: stored.printCenterKeynotes,
  };
}
